import { useNetwork } from '@nftx/react';
import { useCallback, useEffect, useState } from 'react';
import useAddress from './read/web3/useAddress';
import { parseJson } from 'nftx.js';
import { useQueryClient } from '@tanstack/react-query';

// Create a websocket connection and listen for vault updates
// When there's a change, we want to clear our react-query cache and re-fetch everything

const connectWebsocket = (cb: (ws: WebSocket) => void) => {
  const uri =
    process.env.NEXT_PUBLIC_NFTX_WEBSOCKET_URL || 'wss://api-v3.nftx.xyz/ws/';
  const ws = new WebSocket(uri);
  ws.onopen = () => {
    console.debug('Connected to websocket');
  };
  ws.onclose = () => {
    console.debug('Websocket closed, attempting to reconnect');
    setTimeout(() => {
      connectWebsocket(cb);
    }, 2500);
  };
  ws.onerror = (e) => {
    console.error('Socket encountered error');
    ws.close();
  };
  cb(ws);
};

const useSocketSync = () => {
  const [ws, setWs] = useState<WebSocket>();
  const network = useNetwork();
  const userAddress = useAddress();
  const client = useQueryClient();

  const [messageQueue, setMessageQueue] = useState<string[]>([]);

  const onMessage = useCallback(
    async (data: { type: string; payload: any }) => {
      await new Promise<void>((res) => setTimeout(res, 3000));

      switch (data.type) {
        case 'vault_updated':
        case 'collection_updated':
          // Just blanket-invalidate everything and refetch
          client.invalidateQueries();
          break;
        // The websocket emits changed events for individual pools
        // In theory we could optimistically update our queries with the data
        // But for a first pass I think we're fine just invalidating all queries when the vault updates
        case 'liquidity_pool_updated':
        case 'inventory_pool_updated':
        case 'liquidity_position_updated':
        case 'inventory_position_updated':
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!messageQueue.length) {
      return;
    }
    if (!ws || ws.readyState !== ws.OPEN) {
      const h = setTimeout(() => {
        setMessageQueue((queue) => [...queue]);
      }, 3000);
      return () => clearTimeout(h);
    }
    const message = messageQueue[0];
    ws.send(message);
    setMessageQueue(([, ...queue]) => queue);
  }, [messageQueue, ws]);

  const sendMessage = useCallback((data: Record<string, any>) => {
    setMessageQueue((queue) => [...queue, JSON.stringify(data)]);
  }, []);

  useEffect(() => {
    connectWebsocket((ws) => {
      ws.onmessage = (e) => {
        try {
          const data = parseJson<{ type: string; payload: any }>(e.data);

          onMessage(data);
        } catch (e) {
          console.error(e);
        }
      };
      setWs(ws);
    });
  }, [onMessage]);

  // Inform the server of our current network and wallet
  useEffect(() => {
    sendMessage({ type: 'set_client', network, userAddress });
  }, [network, userAddress, sendMessage]);
};

export default useSocketSync;
